import React, { useState, useContext, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import {
  TextInput,
  TextAreaInput,
  Base64FileInput,
} from "../../react-hook-form-inputs";
import { PortalizedModalContext } from "./../../PortalizedModal";
import _ from 'underscore.string'
import { renderRailsErrors } from "../../../utils/rails_error_parser";
import {
  BASE_CACHEKEY_MAP,
  addPitchSection,
  updatePitchSection,
  deletePitchSection,
} from "./../queries";
import {
  IconClose,
  IconEdit,
} from './../../../assets/images/icons'

const Media =({media, mediaEditHandler, mediaDeleteHandler})=>(
  <div key={media.id} className="single-existing-media-mention mb-3">
    <a href={media.link} target="_blank" className="single-existing-pitch-content">
      <div className="existing-media-mention-icon-box">
        <img src={media.logo_url} alt="icon" />
      </div>
      <div className="existing-media-mention-content-box">
        <div className="inner1">
          <p className="existing-media-mention-title poppins mb-0">
            {_.prune(media.title, 25, '...')}
          </p>
          <span className="existing-media-mention-subtitle poppins fs-10">
            {_.prune(media.description, 25, '...')}
          </span>
        </div>
      </div>
    </a>
    <div className="existing-pitch-actions">
      <img
        onClick={() => mediaEditHandler(media) }
        src={IconEdit}
        alt="edit icon"
      />
      <img
        onClick={() => mediaDeleteHandler(media.id) }
        src={IconClose}
        alt="close icon"
      />
    </div>
  </div>
)

const MediaMentionSchema = Yup.object().shape({
  media_mention_title: Yup.string().required("Title is required."),
  media_mention_description: Yup.string().required("Description is required."),
  media_image: Yup.object()
    .required("product image is required.")
    .typeError("product image is required."),
  media_url_link: Yup.string()
    .required("Media url is required.")
    .url("Invalid media url."),
  media_name: Yup.string()
    .required("Media name is required.")
});

const MediaMentions = ({ pitch, pitchableType, pitchableId }) => {
  const deleteMediaModal = useContext( PortalizedModalContext );
  const mediaMentions = pitch.media_mentions || [];
  const [mediaForEdit, setMediaForEdit] = useState(null);
  const queryClient = useQueryClient();

  const methods = useForm({
    resolver: yupResolver(MediaMentionSchema),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      media_mention_title: "",
      media_mention_description: "",
      media_image: null,
      media_url_link: "",
      media_name: ""
    },
  });

  useEffect(()=>{
    if(mediaForEdit){
      methods.setValue("media_mention_title", mediaForEdit.title)
      methods.setValue("media_mention_description", mediaForEdit.description)
      methods.setValue("media_image", {data: mediaForEdit.logo_url} )
      methods.setValue("media_url_link", mediaForEdit.link)
      methods.setValue("media_name", mediaForEdit.media_name)
    }else {
      methods.reset()
    }
  },[mediaForEdit, methods])

  const deleteMediaMention = useMutation(deletePitchSection, {
    onSuccess: () => {
      queryClient.invalidateQueries([BASE_CACHEKEY_MAP[pitchableType], pitchableId]);
      toast.success(`Media deleted successfully.`);
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function mediaDeleteHandler (id){
    deleteMediaModal.setModalContent(
      `Delete Media`,
      <>
        <div className="px-3 pt-3">
          <p>Are you sure you want to delete the media?</p>
        </div>
        <div className="alert-modal-footer pb-3">
          <button className="zeitung-mono weight-300 mr-3 btn-black-outline" onClick={() => deleteMediaModal.closeModal()}>Cancel</button>
          <button className="zeitung-mono weight-300 btn-black"
            onClick={() => {
              deleteMediaMention.mutate({
                section_id: id,
                type: "media_mention",
              });
              if(id === mediaForEdit?.id){
                setMediaForEdit(null)
              }
              deleteMediaModal.closeModal()
            }}
          >Confirm</button>
        </div>
      </>
    );
    deleteMediaModal.openModal();
  };
  const saveMedia = useMutation(
    mediaForEdit ? updatePitchSection : addPitchSection,
    {
      onSuccess: (values) => {
        methods.reset()
        mediaForEdit && setMediaForEdit(null);
        queryClient.invalidateQueries([BASE_CACHEKEY_MAP[pitchableType], pitchableId]);
        toast.success(
          `Media ${mediaForEdit ? "updated" : "added"} successfully.`
        );
      },
      onError: (error) => {
        renderRailsErrors(error);
      },
    }
  );

  const onSubmit = (values) => {
    let id = mediaForEdit?.id ?? pitchableId;
    let pitch_section = {
      title: values.media_mention_title,
      description: values.media_mention_description,
      link: values.media_url_link,
      logo: values.media_image,
      media_name: values.media_name
    }
    if(mediaForEdit?.logo_url === values.media_image.data)
      delete pitch_section.logo;

    saveMedia.mutate({
      pitchable_type: pitchableType,
      pitchable_id: id,
      values: {
        type: "media_mention",
        pitch_section: pitch_section
      },
    });
  };

  return (
    <div className="media-mention-maker">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="add-media-mention mb-4"
        >
          <h6 className="weight-600">Add new media</h6>
          <TextInput
            name="media_mention_title"
            placeholder="Title"
            type="text"
          />
          <TextAreaInput
            name="media_mention_description"
            placeholder="Description"
            type="text"
          />
          <Base64FileInput
            name="media_image"
            placeholder="Upload media image"
            accept="image/*"
          />
          <TextInput
            name="media_url_link"
            placeholder="Media url link"
            type="text"
          />
          <TextInput
            name="media_name"
            placeholder="Media name"
            type="text"
          />
          <button type="submit" className="mx-auto btn btn-outline-dark rounded-0">
            {mediaForEdit ? "UPADTE MEDIA" : "ADD MEDIA"}
          </button>
          {mediaForEdit && (
            <button
              onClick={() => {
                setMediaForEdit(null);
              }}
              type="button"
              className="mx-auto btn btn-outline-dark rounded-0 w-25 ms-3"
            >
              CANCEL
            </button>
          )}
        </form>
      </FormProvider>
      {mediaMentions.length > 0 &&
        <div className="existing-media-mentions">
          <h6 className="fs-10 montserrat weight-600">Existing media</h6>
          {mediaMentions.map((media) => (
            <Media
              key={media.id}
              media={media}
              mediaEditHandler={setMediaForEdit}
              mediaDeleteHandler={mediaDeleteHandler}
            />
          ))}
        </div> }
    </div>
  );
};

MediaMentions.defaultProps = {};

MediaMentions.propTypes = {
  pitch: PropTypes.object.isRequired,
  pitchableType: PropTypes.string.isRequired,
  pitchableId: PropTypes.string.isRequired,
};

export default MediaMentions;
